/**
 * Data repository — the single seam the app's data helpers go through.
 *
 * Behaviour is deliberately conservative to avoid any online regression:
 *   - Web build, or non-offline URLs, or while ONLINE  → the original axios path,
 *     completely unchanged.
 *   - Desktop + OFFLINE + an offline-capable URL        → the local SQLite cache
 *     via the main process (reads from cache; writes are written-through + queued
 *     and pushed automatically on reconnect).
 *   - Reads additionally fall back to the cache if a network call fails outright
 *     (no response), so a stale "online" flag never strands a read.
 *
 * The exported function names/signatures match the old axiosClient exactly, so
 * `helpers/axiosClient` simply re-exports these and the 27 saga modules are
 * unchanged.
 */

import api from '../helpers/api';
import { isDesktop, apiBaseUrl } from '../setup/desktopApi';

// Endpoints the offline store can serve (mirror of the main entity registry).
const OFFLINE_ENTITIES = new Set([
  'selfBill', // pilot transactional entity (read + write offline)
  'store-sales', // POS bill: read (list/view) + write (create + confirm/edit) offline
  'customerPayment', // read + write offline (settles against cached open invoices)
  'salesInvoice', // read-only offline: the customer's open/overdue invoices to settle
  'customerTransaction', // read-only offline: the customer ledger (open rows only)
  // Purchase / supplier side (parity with the sales entities above)
  'purchaseSelfBill', // read + write offline (Supply Self Bill: create + confirm)
  'supply-quotes', // read + write offline (Supply Quote: create + status change)
  'supplierPayment', // read + write offline (Make Payment)
  'purchaseInvoice', // read-only offline: Supplier Invoice History
  'supplierTransaction', // read-only offline: the supplier ledger (open rows only)
  'activity-counters', // read-only offline: manual invoice-number prefix for self-bill Create
  // master data (read-only offline)
  'country', 'state', 'city', 'areas', 'pincode', 'currency', 'unit', 'mop',
  'hsns', 'productgst', 'taxcode', 'cashdiscount', 'locations',
  'product-categories', 'product-groups', 'client-products',
  'customerProfile', 'supplierProfile', 'team',
  // customer-form dropdowns
  'tax-types', 'partygst', 'customer-group',
  // supplier-form + shared dropdowns
  'supplier-group', 'language', 'othertaxes',
  // single-record screens
  'businessProfile',
  // Store Sales sign-in dropdowns (POS list + per-POS users)
  'point-of-sales', 'point-of-sale-users',
]);

// Entities that mutate offline (create / update / confirm are written-through + queued).
const MUTABLE_OFFLINE = new Set([
  'selfBill', 'store-sales', 'customerPayment',
  'purchaseSelfBill', 'supply-quotes', 'supplierPayment',
  // Master profiles that are also editable offline (Add/Edit Customer + Supplier).
  'customerProfile', 'supplierProfile',
]);

// Connectivity flag: start from the browser, refine via the main-process monitor.
let online = typeof navigator === 'undefined' ? true : navigator.onLine;

if (isDesktop()) {
  try {
    window.electron.sync.configure({ baseUrl: apiBaseUrl });
    window.electron.sync.onStatus((s) => {
      if (s && typeof s.online === 'boolean') online = s.online;
    });
  } catch (e) {
    /* older preload without sync surface */
  }
  if (typeof window !== 'undefined') {
    // NIC-up doesn't mean the backend is reachable — let the main-process ping be
    // the source of truth for `online=true`. Just trigger a re-check here.
    window.addEventListener('online', () => {
      try {
        window.electron.sync.now();
      } catch (e) {
        /* noop */
      }
    });
    // NIC-down is unambiguous: go offline immediately.
    window.addEventListener('offline', () => {
      online = false;
    });
  }
}

/** Current connectivity as tracked by the main-process monitor (desktop) or the
 *  browser (web). Lets screens branch their behaviour while offline. */
export function isOnline() {
  return online;
}

// Delegates to pathSegs so ABSOLUTE urls resolve identically to relative ones: 31 files
// build `${process.env.REACT_APP_API_URL}/x`, and splitting those naively yielded
// segs[0] === 'https:', so offline-capable entities were never matched.
function parts(url) {
  return pathSegs(url);
}

// --- Response snapshot cache (universal offline read fallback) ---------------
// Anything the entity store can't serve offline (reports, dashboards, and every
// other screen with no cached entity) is snapshotted: its last successful ONLINE
// response is stored verbatim (keyed by full URL, in the main SQLite cache) and
// replayed offline. So "whatever you opened online works offline" — as a snapshot
// of what was last seen. Replay ONLY fires for URLs that would otherwise 503
// offline, so it can never make a request worse than it is today. Reports also get
// an "as of <date>" stamp (see isReportUrl + the ReportModal banner).
const REPORT_ENDPOINTS = new Set([
  'customerDueReport', 'customerInvoiceReport', 'inactiveCustomer', 'inactiveCustomers',
  'productWiseAnalysis', 'productWiseReport', 'salesdashboard', 'purchasedashboard',
  'paymentStatements', 'staffSalesCollection', 'topSupplyProducts', 'inventory-transactions',
  'salesPriceDeviationReport', 'stockTransferReport', 'salesReport', 'purchaseReport',
]);
const REPORT_RE = /(report|dashboard|statement|analysis|deviation|leaderboard)/i;

// The LoopBack base path carried by the configured API URL ('/api' in every environment).
const API_BASE_PATH = (() => {
  try {
    return new URL(apiBaseUrl).pathname.replace(/\/+$/, '');
  } catch (e) {
    return '';
  }
})();

/**
 * Path segments of a request URL, relative to the API root. Absolute urls
 * (`${REACT_APP_API_URL}/x`, built at 91 call sites) carry both the origin AND the
 * '/api' base path; stripping only the origin left segs[0] === 'api', so entity
 * routing, report detection and the POS offline defaults never matched them.
 * Relative urls are resolved against a base that already includes '/api', so a
 * leading 'api' segment there is left alone.
 */
function pathSegs(url) {
  let u = String(url);
  const scheme = u.indexOf('://');
  if (scheme >= 0) {
    const afterHost = u.slice(scheme + 3);
    const slash = afterHost.indexOf('/');
    u = slash >= 0 ? afterHost.slice(slash) : '';
    const path = u.split('?')[0];
    if (API_BASE_PATH && (path === API_BASE_PATH || path.startsWith(`${API_BASE_PATH}/`))) {
      u = u.slice(API_BASE_PATH.length);
    }
  }
  return u.split('?')[0].replace(/^\/+/, '').split('/').filter(Boolean);
}

/** Is this URL a report/aggregation endpoint eligible for the snapshot cache? */
function isReportUrl(url) {
  const segs = pathSegs(url);
  const seg = segs[0] || '';
  if (seg === 'stock-on-hands') return segs[1] === 'summary'; // the value report only, not stock lookups
  return REPORT_ENDPOINTS.has(seg) || REPORT_RE.test(seg);
}

// Server-computed endpoints that back the Store Sales create screen. They can't be
// reproduced from the entity store, so they rely on the snapshot cache (replay the
// last online response). But on a genuine cache MISS — a product/qty/unit never seen
// online — a 503 would leave the create screen broken: the price call's failure
// never clears the product grid's `productId` latch, so the grid stays disabled and
// no further tiles are clickable. Returning a benign EMPTY default instead keeps the
// screen interactive (the line falls back to the product's own price / no live
// stock+batch), matching how the screen already behaves online for a product with no
// agreements/stock/batches. `empty` is the shape the caller destructures.
const POS_LOOKUP_DEFAULTS = [
  { seg: 'saledprice', empty: {} },
  { seg: 'stock-on-hands', sub: 'byProduct', empty: [] },
  { seg: 'product-batch-numbers', sub: 'byProduct', empty: [] },
  { seg: 'product-unit-conversions', empty: [] },
  { seg: 'unit-conversions', empty: [] },
  { seg: 'cataloguesForClientProduct', empty: [] },
];

/** The benign offline default for a POS lookup URL, or undefined if not one. */
function posLookupDefault(url) {
  const segs = pathSegs(url);
  const seg = segs[0] || '';
  const hit = POS_LOOKUP_DEFAULTS.find((d) => d.seg === seg && (!d.sub || segs[1] === d.sub));
  return hit ? hit.empty : undefined;
}

// Timestamp (ms) of the most recently replayed report snapshot, so the report UI
// can show "showing data as of <date>" while offline. One report is open at a time,
// so a single value tracks the report currently on screen.
let lastReportSnapshotAt = null;

/** ms timestamp of the report snapshot currently on screen (offline), or null. */
export function getReportSnapshotAt() {
  return lastReportSnapshotAt;
}

/** Fire-and-forget: persist a successful online response for offline replay. */
function captureSnapshot(url, res) {
  if (!isDesktop() || !res) return;
  try {
    const p = window.electron.data.cacheResponse({ url, status: res.status, body: res.data });
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch (e) {
    /* no desktop cache surface (older preload) */
  }
}

// '__report__:<endpoint>' key under which a report's LATEST snapshot is ALSO
// mirrored. Report URLs are parameterised (due date, customer, group, filters), so
// an exact-URL match almost never hits offline; this endpoint-level key lets an
// offline request fall back to the last version of that report the user downloaded
// online (stamped "as of <date>"), instead of a blank screen.
function reportKey(url) {
  return `__report__:${pathSegs(url)[0] || ''}`;
}

/** Return the cached snapshot as an axios-shaped result, or null on a miss. */
async function getSnapshot(lookupUrl, markReport = false) {
  if (!isDesktop()) return null;
  try {
    const snap = await window.electron.data.getCachedResponse(lookupUrl);
    if (snap && snap.body != null) {
      if (markReport) lastReportSnapshotAt = snap.updatedAt || null; // report "as of" banner
      return { data: snap.body, status: snap.status || 200, __snapshotAt: snap.updatedAt || null };
    }
  } catch (e) {
    /* ignore — treat as a miss */
  }
  return null;
}

/** Exact-URL snapshot first; for a report, fall back to its last-downloaded
 *  version (endpoint-level) so a param mismatch still shows data offline. */
async function getReadSnapshot(url) {
  const report = isReportUrl(url);
  const exact = await getSnapshot(url, report);
  if (exact) return exact;
  if (report) return getSnapshot(reportKey(url), true);
  return null;
}

function offlineCapable(method, url) {
  const segs = parts(url);
  if (!OFFLINE_ENTITIES.has(segs[0])) return false;
  // /tax-types/byCountry/{countryId} is a filtered collection (GST Type dropdown),
  // not a resource action — keep it offline-capable despite the 3rd segment.
  const isTaxTypesByCountry = segs[0] === 'tax-types' && segs[1] === 'byCountry';
  // Store Sales "confirm"/edit addresses the resource as store-sales/{id}/{paymentId}
  // (the 3rd segment is the customer-payment id, not a resource action) — allow it.
  const isStoreSalesEdit = segs[0] === 'store-sales' && segs.length === 3;
  if (segs.length > 2 && !isTaxTypesByCountry && !isStoreSalesEdit) return false; // resource actions (e.g. selfBill/{id}/clone) stay online
  const m = (method || 'get').toLowerCase();
  if (m === 'get') return true;
  return MUTABLE_OFFLINE.has(segs[0]);
}

async function viaMain(method, url, payload) {
  const res = await window.electron.data.request({ method, url, payload });
  if (res && res.status >= 400) {
    const err = new Error(`offline request failed (${res.status})`);
    err.response = { status: res.status, data: res.data };
    throw err;
  }
  return { data: res.data, status: res.status };
}

function routeOffline(method, url) {
  return isDesktop() && !online && offlineCapable(method, url);
}

/** Reads: route to cache when offline; also fall back to cache on a network failure. */
async function read(method, url) {
  if (routeOffline(method, url)) return viaMain(method, url);
  // Desktop + authoritatively offline + not cacheable → fail fast instead of letting
  // an un-cacheable request hang forever (so screens like the Price Lists show their
  // empty/error state rather than spinning). Guarded strictly on the main-process
  // `online` flag, so it never fires on a transient/ambiguous online hiccup.
  if (isDesktop() && !online && !offlineCapable(method, url)) {
    // Not servable from the entity store offline → replay the last online snapshot
    // (exact URL, or the report's last-downloaded version); fall through to 503
    // only on a genuine cache miss.
    const snap = await getReadSnapshot(url);
    if (snap) return snap;
    // POS create lookups must never 503 offline (a 503 wedges the product grid) —
    // serve a benign empty default on a snapshot miss.
    const posDefault = posLookupDefault(url);
    if (posDefault !== undefined) return { data: posDefault, status: 200 };
    const err = new Error(`Offline: "${url}" is not available without a connection`);
    err.response = { status: 503, data: { error: { message: 'Offline — this data is not available without a connection.' } } };
    throw err;
  }
  try {
    const res = await api.get(url);
    // Snapshot every successful online read the entity store can't serve live, so
    // it is available offline. (Entity-backed reads are served live from the cache
    // offline, so they don't need — and don't get — a snapshot.) Reports are ALSO
    // mirrored under their endpoint key so an offline param mismatch still resolves.
    if (!offlineCapable('get', url)) {
      captureSnapshot(url, res);
      if (isReportUrl(url)) captureSnapshot(reportKey(url), res);
    }
    return res;
  } catch (err) {
    if (isDesktop() && offlineCapable(method, url) && !err.response) {
      return viaMain(method, url);
    }
    // A network failure (no response) while the online flag was still set — serve
    // the last snapshot rather than a broken screen, if we captured one.
    if (isDesktop() && !err.response) {
      const snap = await getReadSnapshot(url);
      if (snap) return snap;
      // Same guard as the offline branch: a POS create lookup must degrade to its
      // empty default, never a hard failure that wedges the product grid.
      const posDefault = posLookupDefault(url);
      if (posDefault !== undefined) return { data: posDefault, status: 200 };
    }
    throw err;
  }
}

/** Writes: route to the queue when offline (flag-based — never on an ambiguous
 *  network error, to avoid a duplicate server write). */
async function write(method, url, payload, axiosCall) {
  if (routeOffline(method, url)) return viaMain(method, url, payload);
  return axiosCall();
}

export async function getRequest(URL) {
  return read('get', URL);
}

export async function postRequest(URL, payload) {
  return write('post', URL, payload, () => api.post(URL, payload));
}

export async function putRequest(URL, payload) {
  return write('put', URL, payload, () => api.put(URL, payload));
}

export async function patchRequest(URL, payload) {
  return write('patch', URL, payload, () => api.patch(URL, payload));
}

export async function deleteRequest(URL) {
  return write('delete', URL, undefined, () => api.delete(URL));
}

// Not offline-capable in the pilot — always the original axios behaviour.
export async function uploadRequest(URL, payload, headers) {
  return api.post(URL, payload, headers);
}

export async function postDeleteRequest(URL, payload) {
  return api.delete(URL, { data: payload });
}
